import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { getKidSub } from '@/auth/authStore';

import { getStudyDocumentPageImage } from './studyAnalysisApi';

/**
 * The page images an analysis was read from. The capture flow keeps the PNGs it uploaded in
 * memory; otherwise (a reload, another device) the page is the stored PNG the server read. The
 * page is never redrawn from the original: region coordinates are fractions of the stored PNG,
 * and a photo's rotation exists only in that PNG.
 */

const pageImages = new Map<string, Blob>();
const speakerNotes = new Map<string, string>();

/** Scoped to the signed-in learner, so a sibling on the same device never sees another's page. */
function pageKey(documentId: string, pageNumber: number) {
  return `${getKidSub() ?? 'anonymous'}:${documentId}:${pageNumber}`;
}

export function rememberPageImage(documentId: string, pageNumber: number, image: Blob): void {
  pageImages.set(pageKey(documentId, pageNumber), image);
}

async function fetchStoredPage(documentId: string, pageNumber: number): Promise<Blob> {
  const key = pageKey(documentId, pageNumber);
  const page = await getStudyDocumentPageImage(documentId, pageNumber);
  const response = await fetch(page.read_url);
  if (!response.ok) throw new Error('This page could not be opened. Try again.');
  const image = await response.blob();
  if (key !== pageKey(documentId, pageNumber))
    throw new Error('Your account changed. Reopen the source.');
  if (page.speaker_notes) speakerNotes.set(key, page.speaker_notes);
  return image;
}

export async function loadPageImage(documentId: string, pageNumber: number): Promise<Blob> {
  const key = pageKey(documentId, pageNumber);
  const cached = pageImages.get(key);
  if (cached) return cached;
  const image = await fetchStoredPage(documentId, pageNumber);
  if (key !== pageKey(documentId, pageNumber))
    throw new Error('Your account changed. Reopen the source.');
  rememberPageImage(documentId, pageNumber, image);
  return image;
}

/** A page image and an object URL for it. The URL is revoked when the page changes. */
export function usePageImage(page: { document_id: string; page_number: number } | null) {
  const query = useQuery({
    queryKey: ['study-analysis-page-image', getKidSub(), page?.document_id, page?.page_number],
    queryFn: () => loadPageImage(page!.document_id, page!.page_number),
    enabled: page !== null,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data || typeof URL.createObjectURL !== 'function') {
      setUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(query.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);
  return {
    image: query.data ?? null,
    url,
    loading: query.isLoading,
    error: query.isError,
    speakerNotes: page
      ? (speakerNotes.get(pageKey(page.document_id, page.page_number)) ?? null)
      : null,
  };
}
